import _ from "lodash";

import { vsComponentName as userDetailComponentName } from "./pages/user";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";
import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import { AuthorityGroup, Role } from "./model/authorityGroup";
import { UserInfo } from "../application/UserProfile";
import {
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../records/views/recordGridView";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../records/grids/settings/gridSettingsService";

import { vsObjectName as userObjectName } from "./config/user";
import { vsObjectName as roleObjectName } from "./config/roles";
import { vsObjectName as userRolePermissionObjectName } from "./config/userRolePermission";
import { IRowCommandEventArgs } from "../records/grids/controller";
import {
  FormViewConfig,
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
  vsComponentName as formViewComponentName,
} from "../records/forms/formView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../records/grids/columns/columnDefinitionService";
import { vsComponentName as roleDetailComponentName } from "./pages/role";
import { ColDef } from "../records/grids/columns/colDef";
import { FilterProvider } from "../records/grids/columns/filters/FilterProvider";
import { ICompileService, IQService } from "angular";
import { OptionsFilter } from "../records/grids/columns/filters/subFilters/OptionsFilter";
import { CloneRecordResponse, ServiceResponse } from "../records/server/query";
import { ApplicationInfo } from "../application/appInfo";

import { apiKeyStateDefinition } from "./config/apiKey";
import {
  vsComponentName as configurableGridFormViewComponentName,
  ConfigurableGridFormViewConfig,
} from "../records/views/configurableGridFormView";

config.$inject = ["$stateProvider", "applicationInfo"];
export function config(
  $stateProvider: StateProvider,
  applicationInfo: ApplicationInfo
) {
  $stateProvider
    .state("main.user", {
      url: "user",
      template: "<user-profile-page></user-profile-page>",
    })
    .state("main.userApiKeys", {
      url: "user/apiKeys",
      component: "userProfileApiKeyPage",
    })
    .state("main.users", {
      url: "users",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        roles: resolveRoles,
        authorityGroups: resolveAuthorityGroup,
      },
    })
    .state("main.users.grid", {
      component: RecordGridViewComponentName,
      url: "",
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService) =>
            recordGridViewService.getGridConfiguration(userObjectName),
        ],
        gridDataSource: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.gridDataSource,
        ],
        gridSettings: [
          GridSettingsServiceName,
          (gridSettingsService: GridSettingsService) =>
            gridSettingsService.getGridSettings(Symbol("main.users.grid")),
        ],
        columns: [
          "gridConfiguration",
          "$compile",
          ColumnDefinitionServiceName,
          (
            gridConfiguration: GridConfiguration,
            $compile: ICompileService,
            columnDefinitionService: ColumnDefinitionService
          ) => [
            columnDefinitionService.getLinkColDef({
              headerName: "Info",
              iconClass: "fa fa-info-circle text-primary",
              colId: "info",
              requiresPermission: "userModify",
              width: 20,
              stateExpression: "^.detail.roles({userId: data.UserAuthId})",
            }),
            columnDefinitionService.getLinkColDef({
              headerName: "Edit",
              iconClass: "fa fa-pencil text-primary",
              colId: "edit",
              requiresPermission: "userModify",
              width: 20,
              stateExpression: "^.edit({userId: data.UserAuthId})",
            }),
            ...(gridConfiguration.columns ?? []),
            <ColDef>{
              colId: "lockUser",
              headerName: "Locked Status",
              field: "LockedDate",
              template: `<button class="btn" ng-class="{\'btn-success\': !data.LockedDate, \'btn-danger\': !!data.LockedDate}" ng-click="$emit('command', {data, commandName: 'userLock'})">
                <i class="fa" ng-class="{\'fa-unlock\': !data.LockedDate, \'fa-lock\': !!data.LockedDate}"></i> {{data.LockedDate ? \'Unlock\' : \'Lock\'}}
              </button>`,
              requiresPermission: "userLock",
              filter: new FilterProvider("LockedDate", $compile)
                .addFilter(
                  "=",
                  new OptionsFilter({
                    displayName: "=",
                    queryFieldNames: ["Locked"],
                    options: [
                      { display: "Locked", value: true },
                      { display: "Unlocked", value: false },
                    ],
                  })
                )
                .getGridFilterFactory(),
            },
            {
              colId: "resetPassword",
              headerName: "Reset Password",
              template: `
              <button type="button" class="btn btn-primary" ng-click="$emit('command', {data, commandName: 'resetPassword'})"
                >Reset</button>`,
              requiresPermission: "userModify",
              suppressSorting: true,
              suppressMenu: true,
            },
            {
              colId: "setPassword",
              headerName: "Set Password",
              template: `<button type="button" class="btn btn-primary" ng-click="$emit('command', {data, commandName: 'setPassword'})"
            if-has-permissions="'userPasswordSet'">
            Set
          </button>`,
              suppressSorting: true,
              suppressMenu: true,
            },
          ],
        ],
        defaultSort: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.defaultSort,
        ],
        nameSingular: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.nameSingular,
        ],
        onItemCommand: [
          ServerUtilityName,
          "$dialog",
          "$uibModal",
          (
            serverUtility: ServerUtility,
            $dialog: any,
            $uibModal: angular.ui.bootstrap.IModalService
          ) => [
            "args",
            (args: IRowCommandEventArgs<UserInfo>) => {
              const row = args.data;
              const gridApi = args.gridApi;
              switch (args.commandName) {
                case "userLock":
                  const currentlyLocked = !!row.LockedDate;
                  serverUtility
                    .post<UserInfo>({
                      url: `/api/user/${row.UserAuthId}/lock`,
                      postObject: { Lock: !currentlyLocked },
                    })
                    .then((profile) => {
                      row.LockedDate = profile.LockedDate;
                      gridApi.refreshView();
                    });
                  break;
                case "resetPassword":
                  serverUtility
                    .post<void>({
                      url: "/api/user/forgotpassword",
                      postObject: {
                        Username: row.UserName,
                      },
                    })
                    .then(() => {
                      return $dialog({
                        text: "Password was reset",
                        title: "Password reset",
                      });
                    });
                  break;
                case "setPassword":
                  $uibModal.open({
                    template: `
                  <div class="modal-header">
                    <h3 class="modal-title" id="modal-title">Set Password</h3>
                  </div>
                  <div class="modal-body" id="modal-body">
                    <form>
                      <div class="form-group">
                        <label for="user">User</label>
                        <p class="form-control-static">
                          {{userInfo.FirstName}} {{userInfo.LastName}}
                        </p>
                      </div>
                      <div class="form-group">
                        <label for="password">Password</label>
                        <input class="form-control" type="password" ng-model="password" />
                      </div>
                    </form>
                  </div>
                  <div class="modal-footer">
                    <button class="btn btn-primary" type="button" ng-click="setPassword(password)">OK</button>
                    <button class="btn btn-warning" type="button" ng-click="$dismiss()">Cancel</button>
                  </div>
                    `,
                    controller: [
                      "$scope",
                      "userInfo",
                      ServerUtilityName,
                      "$uibModalInstance",
                      (
                        $scope: any,
                        userInfo: UserInfo,
                        serverUtility: ServerUtility,
                        $uibModalInstance: angular.ui.bootstrap.IModalInstanceService
                      ) => {
                        $scope.userInfo = userInfo;
                        $scope.setPassword = (password: string) => {
                          serverUtility
                            .sendApiRequest<void>({
                              url: "/api/user/setPassword",
                              method: "PUT",
                              data: {
                                UserAuthId: userInfo.UserAuthId,
                                Password: password,
                              },
                            })
                            .then(() => {
                              $uibModalInstance.close();
                            });
                        };
                      },
                    ],
                    resolve: {
                      userInfo: args.data,
                      onPasswordSet: () => {},
                    },
                  });
              }
            },
          ],
        ],
        showAddButton: [() => true],
        showAddButtonPermission: [() => "userModify"],
        onAddNew: [
          "$state",
          ($state: StateService) => () => $state.go("^.add"),
        ],
      },
    })
    .state("main.users.add", {
      url: "/add",
      component: formViewComponentName,
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(userObjectName),
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        model: [
          () => {
            return {};
          },
        ],
        title: [() => "Create New User"],
        onSubmit: [
          "formViewConfig",
          "$state",
          (formViewConfig: FormViewConfig, $state: StateService) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: UserInfo) =>
              formViewConfig.submitForm!({ formViewController, model }).then(
                (user: UserInfo) =>
                  $state.go("^.detail.roles", { userId: user.UserAuthId })
              ),
          ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
      },
    })
    .state("main.users.detail", {
      url: "/{userId:int}",
      component: userDetailComponentName,
      params: {
        userId: null,
      },
      resolve: {
        user: resolveUser,
      },
    })
    .state("main.users.edit", {
      url: "/{userId:int}/edit",
      component: formViewComponentName,
      params: {
        userId: null,
      },
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(userObjectName),
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        model: [
          ServerUtilityName,
          "$stateParams",
          (serverUtility: ServerUtility, $stateParams: StateParams) =>
            serverUtility.getById({
              url: "/api/user",
              id: $stateParams.userId,
            }),
        ],
        onSubmit: [
          "formViewConfig",
          "$state",
          (formViewConfig: FormViewConfig, $state: StateService) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: UserInfo) =>
              formViewConfig.submitForm!({ formViewController, model }).then(
                (user: UserInfo) =>
                  $state.go(
                    "^.detail.roles",
                    { userId: user.UserAuthId },
                    { relative: "main.users.edit" }
                  )
              ),
          ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
      },
    })
    .state("main.users.roles", {
      url: "/roles",
      template: "<ui-view></ui-view>",
      abstract: true,
    })
    .state("main.users.roles.detail", {
      url: "/{roleId:int}",
      component: roleDetailComponentName,
      params: {
        roleId: null,
      },
      resolve: {
        role: [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) =>
            serverUtility.getById({
              url: "/api/role",
              id: $stateParams.roleId,
            }),
        ],
      },
    })
    .state("main.users.roles.grid", {
      url: "",
      component: RecordGridViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService) =>
            recordGridViewService.getGridConfiguration(roleObjectName),
        ],
        gridDataSource: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.gridDataSource,
        ],
        gridSettings: [
          GridSettingsServiceName,
          (gridSettingsService: GridSettingsService) =>
            gridSettingsService.getGridSettings(
              Symbol("main.users.roles.grid")
            ),
        ],
        columns: [
          "gridConfiguration",
          ColumnDefinitionServiceName,
          (
            gridConfiguration: GridConfiguration,
            columnDefinitionService: ColumnDefinitionService
          ) => {
            return [
              columnDefinitionService.getLinkColDef({
                headerName: "Detail",
                iconClass: "fa fa-info-circle text-primary",
                colId: "detail",
                requiresPermission: "userModify",
                width: 20,
                stateExpression: "^.detail.permissions({roleId: data.Id})",
              }),
              columnDefinitionService.getLinkColDef({
                headerName: "Edit",
                iconClass: "fa fa-pencil text-primary",
                colId: "edit",
                requiresPermission: "userModify",
                width: 20,
                stateExpression: "^.edit({roleId: data.Id})",
              }),
              ...(gridConfiguration.columns ?? []),
            ];
          },
        ],
        defaultSort: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.defaultSort,
        ],
        nameSingular: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.nameSingular,
        ],
        showAddButton: [() => true],
        showAddButtonPermission: [() => "userModify"],
        onItemCommand: [
          "$state",
          ($state: StateService) => [
            "args",
            (args: IRowCommandEventArgs<Role>) => {
              switch (args.commandName) {
                case "edit":
                  $state.go("^.edit", { roleId: args.data.Id });
                  break;
              }
            },
          ],
        ],
        onAddNew: [
          "$state",
          ($state: StateService) => () => $state.go("^.add"),
        ],
      },
    })
    .state("main.users.roles.add", {
      url: "/add",
      component: formViewComponentName,
      params: {
        cloneId: undefined,
      },
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(roleObjectName),
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        model: [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) => {
            const cloneId: number = $stateParams.cloneId;
            if (!cloneId) {
              return {};
            }

            return serverUtility
              .sendApiRequest<ServiceResponse<CloneRecordResponse<Role>>>({
                method: "GET",
                url: `/api/role/${cloneId}/clone`,
              })
              .then((cloneResponse) => {
                return cloneResponse.Result.Clone;
              });
          },
        ],
        title: [() => "Create Role"],
        onSubmit: [
          "formViewConfig",
          "$state",
          (formViewConfig: FormViewConfig, $state: StateService) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: Role) =>
              formViewConfig.submitForm!({ formViewController, model }).then(
                (result: Role) =>
                  $state.go("^.detail.permissions", { roleId: result.Id })
              ),
          ],
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
      },
    })
    .state("main.users.roles.edit", {
      url: "/{roleId:int}",
      component: formViewComponentName,
      params: {
        roleId: undefined,
      },
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(roleObjectName),
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) => formViewConfig.fields,
        ],
        model: [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) =>
            serverUtility.getById({
              url: "/api/role",
              id: $stateParams.roleId,
            }),
        ],
        title: ["model", (model: Role) => `Edit role ${model.RoleName}`],
        onSubmit: [
          "formViewConfig",
          "$state",
          (formViewConfig: FormViewConfig, $state: StateService) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: Role) =>
              formViewConfig.submitForm!({ formViewController, model }).then(
                (result: Role) =>
                  $state.go("^.detail.permissions", { roleId: result.Id })
              ),
          ],
        ],
        onCancel: [
          "$state",
          "$stateParams",
          ($state: StateService, $stateParams: StateParams) => () =>
            $state.go("^.detail.permissions", { roleId: $stateParams.roleId }),
        ],
      },
    })
    .state("main.users.userPermissions", {
      url: "/userPermissions",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService): GridConfiguration => {
            return recordGridViewService.getGridConfiguration(
              userRolePermissionObjectName
            )!;
          },
        ],
        config: [
          "gridConfiguration",
          (
            gridConfiguration: GridConfiguration
          ): ConfigurableGridFormViewConfig => {
            return {
              gridConfiguration,
              defaultQuery: {},
              additionalToggleButtons: [],
              showNavButton: false,
            };
          },
        ],
      },
    });

  if (applicationInfo.isModuleLoaded("authentication.apiKey")) {
    $stateProvider.state("main.users.apiKey", apiKeyStateDefinition());
  }
}

resolveUser.$inject = ["$stateParams", ServerUtilityName];
function resolveUser($stateParams: StateParams, serverUtility: ServerUtility) {
  const userId = $stateParams.userId;

  return serverUtility.getById<UserInfo>({
    url: "/api/user",
    id: userId,
  });
}

resolveRoles.$inject = [ServerUtilityName];
function resolveRoles(serverUtility: ServerUtility) {
  return serverUtility.fetchAll({
    url: "/api/role",
    query: {
      OrderBy: "AuthorityGroupName,RoleName",
    },
  });
}

resolveAuthorityGroup.$inject = [ServerUtilityName];
function resolveAuthorityGroup(serverUtility: ServerUtility) {
  return serverUtility.fetchAll<AuthorityGroup>({
    url: "/api/authorityGroup",
    query: { OrderBy: "Name" },
  });
}
